import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, Shield, BarChart3, Target, Settings, X, Check, ChevronDown, ChevronUp } from 'lucide-react';

// Google Analytics Measurement ID - configurabile via env
const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || '';
const API = process.env.REACT_APP_BACKEND_URL;

// Funzione per sincronizzare i consensi con il backend GDPR
const syncGDPRConsents = async (prefs) => {
  const token = localStorage.getItem('token');
  if (!token) return; // Utente non loggato, salta la sincronizzazione
  
  try {
    // Mappa le preferenze cookie ai consensi GDPR
    const consentMappings = [
      { cookie: 'marketing', gdpr: 'marketing' },
      { cookie: 'analytics', gdpr: 'analytics' },
      { cookie: 'preferences', gdpr: 'newsletter' } // Newsletter legato a preferenze
    ];
    
    for (const mapping of consentMappings) {
      if (prefs[mapping.cookie] !== undefined) {
        await fetch(`${API}/api/gdpr/consents/${mapping.gdpr}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            consent_type: mapping.gdpr,
            granted: prefs[mapping.cookie]
          })
        });
      }
    }
    console.log('✅ Consensi GDPR sincronizzati');
  } catch (error) {
    console.error('Errore sincronizzazione consensi GDPR:', error);
  }
};

// Funzione per caricare Google Analytics
const loadGoogleAnalytics = (measurementId) => {
  if (!measurementId || typeof window === 'undefined') return;
  
  // Check if already loaded
  if (window.gtag) return;
  
  // Load gtag.js script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);
  
  // Initialize gtag
  window.dataLayer = window.dataLayer || [];
  window.gtag = function() { window.dataLayer.push(arguments); };
  window.gtag('js', new Date());
  window.gtag('config', measurementId, {
    'anonymize_ip': true, // GDPR compliance
    'cookie_flags': 'SameSite=None;Secure'
  });
  
  console.log('✅ Google Analytics caricato');
};

// Funzione per rimuovere Google Analytics
const removeGoogleAnalytics = () => {
  // Remove cookies
  const cookies = document.cookie.split(';');
  for (let cookie of cookies) {
    const name = cookie.split('=')[0].trim();
    if (name.startsWith('_ga') || name.startsWith('_gid') || name.startsWith('_gat')) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    }
  }
  
  // Disable gtag
  if (window.gtag) {
    window['ga-disable-' + GA_MEASUREMENT_ID] = true;
  }
  
  console.log('🚫 Google Analytics disattivato');
};

export const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
    preferences: false
  });

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      setShowBanner(true);
    } else {
      // Apply saved preferences
      const saved = JSON.parse(consent);
      setPreferences(saved);
      
      // Load Google Analytics if analytics cookies were accepted
      if (saved.analytics && GA_MEASUREMENT_ID) {
        loadGoogleAnalytics(GA_MEASUREMENT_ID);
      }
    }
  }, []);

  const saveAndApplyPreferences = async (prefs) => {
    const consent = {
      ...prefs,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookieConsent', JSON.stringify(consent));
    localStorage.setItem('cookiePreferences', JSON.stringify(prefs));
    
    // Apply Google Analytics based on preference
    if (prefs.analytics && GA_MEASUREMENT_ID) {
      loadGoogleAnalytics(GA_MEASUREMENT_ID);
    } else {
      removeGoogleAnalytics();
    }
    
    // Sincronizza con il sistema GDPR del backend (se utente loggato)
    await syncGDPRConsents(prefs);
    
    setShowBanner(false);
    setShowCustomize(false);
  };

  const acceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true
    };
    setPreferences(allAccepted);
    saveAndApplyPreferences(allAccepted);
  };

  const rejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false
    };
    setPreferences(onlyNecessary);
    saveAndApplyPreferences(onlyNecessary);
  };

  const saveCustomPreferences = () => {
    saveAndApplyPreferences(preferences);
  };

  const cookieCategories = [
    {
      id: 'necessary',
      name: 'Cookie Necessari',
      icon: Shield,
      required: true,
      description: 'Essenziali per il funzionamento del sito. Includono autenticazione, sicurezza e memorizzazione del consenso. Non possono essere disattivati.',
      examples: 'session_id, auth_token, cookieConsent, XSRF-TOKEN'
    },
    {
      id: 'analytics',
      name: 'Cookie Analitici',
      icon: BarChart3,
      required: false,
      description: 'Ci aiutano a capire come i visitatori interagiscono con il sito, raccogliendo informazioni in forma anonima e aggregata.',
      examples: '_ga, _gid, _gat (Google Analytics)'
    },
    {
      id: 'marketing',
      name: 'Cookie di Marketing',
      icon: Target,
      required: false,
      description: 'Utilizzati per mostrare annunci pertinenti ai tuoi interessi e misurare l\'efficacia delle campagne pubblicitarie.',
      examples: '_fbp, fr (Facebook), _gcl_au (Google Ads)'
    },
    {
      id: 'preferences',
      name: 'Cookie di Preferenza',
      icon: Settings,
      required: false,
      description: 'Permettono al sito di ricordare le tue scelte (lingua, tema, impostazioni di visualizzazione).',
      examples: 'user_preferences, theme, language'
    }
  ];

  if (!showBanner) return null;

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/60 z-[9998] backdrop-blur-sm" 
        onClick={() => {}} // Prevent closing by clicking overlay
      />
      
      {/* Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-[9999] p-4 md:p-6">
        <div 
          className="max-w-4xl mx-auto rounded-2xl shadow-2xl overflow-hidden"
          style={{ background: '#FFFFFF', border: '1px solid #E5E7EB' }}
        >
          {/* Header */}
          <div className="p-6 pb-4">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                   style={{ background: 'linear-gradient(135deg, #2A9FCB 0%, #2A9FCB 100%)' }}>
                <Cookie className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-bold text-[#2A9FCB] mb-2">
                  Utilizziamo i Cookie 🍪
                </h2>
                <p className="text-sm text-[#2A9FCB]/80 leading-relaxed">
                  Questo sito utilizza cookie tecnici necessari al funzionamento e, previo tuo consenso, 
                  cookie di profilazione e analitici per migliorare la tua esperienza e mostrati contenuti personalizzati.
                  Puoi accettare tutti i cookie, rifiutarli o personalizzare le tue preferenze.
                </p>
                
                {/* GDPR Compliance Badge */}
                <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-50 border border-green-200">
                  <Shield className="w-4 h-4 text-green-600" />
                  <span className="text-xs font-medium text-green-700">
                    Conforme GDPR (UE 2016/679) • Dati sanitari protetti con crittografia AES-256
                  </span>
                </div>
                
                <div className="flex gap-4 mt-2">
                  <Link to="/privacy#cookie-policy" className="text-xs underline" style={{ color: '#2A9FCB' }}>
                    Cookie Policy
                  </Link>
                  <Link to="/privacy" className="text-xs underline" style={{ color: '#2A9FCB' }}>
                    Privacy Policy
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Customize Panel */}
          {showCustomize && (
            <div className="px-6 pb-4 border-t" style={{ borderColor: '#E5E7EB' }}>
              <div className="pt-4 space-y-3">
                {cookieCategories.map((category) => (
                  <div 
                    key={category.id}
                    className="rounded-xl overflow-hidden"
                    style={{ border: '1px solid #E5E7EB' }}
                  >
                    {/* Category Header */}
                    <div 
                      className="p-3 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                      onClick={() => setExpandedCategory(expandedCategory === category.id ? null : category.id)}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          preferences[category.id] ? '' : 'bg-gray-100'
                        }`} style={preferences[category.id] ? { background: '#2A9FCB' } : {}}>
                          <category.icon className={`w-4 h-4 ${preferences[category.id] ? 'text-white' : 'text-[#2A9FCB]/70'}`} />
                        </div>
                        <div>
                          <span className="font-medium text-[#2A9FCB] text-sm">{category.name}</span>
                          {category.required && (
                            <span className="ml-2 text-xs px-2 py-0.5 rounded-full  bg-gray-100 text-[#2A9FCB]/70">
                              Sempre attivi
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {/* Toggle */}
                        {!category.required && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setPreferences(prev => ({ ...prev, [category.id]: !prev[category.id] }));
                            }}
                            className={`w-12 h-6 rounded-full transition-colors relative ${
                              preferences[category.id] ? '' : 'bg-gray-200'
                            }`}
                            style={preferences[category.id] ? { background: '#2A9FCB' } : {}}
                          >
                            <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                              preferences[category.id] ? 'translate-x-7' : 'translate-x-1'
                            }`} />
                          </button>
                        )}
                        {category.required && (
                          <div className="w-12 h-6 rounded-full flex items-center justify-center" style={{ background: '#2A9FCB' }}>
                            <Check className="w-3 h-3 text-white" />
                          </div>
                        )}
                        {expandedCategory === category.id ? (
                          <ChevronUp className="w-4 h-4 text-[#2A9FCB]/60" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-[#2A9FCB]/60" />
                        )}
                      </div>
                    </div>
                    
                    {/* Expanded Details */}
                    {expandedCategory === category.id && (
                      <div className="px-3 pb-3 pt-0">
                        <div className="p-3 rounded-lg text-xs" style={{ background: '#F9FAFB' }}>
                          <p className="text-[#2A9FCB]/80 mb-2">{category.description}</p>
                          <p className="text-[#2A9FCB]/60">
                            <strong>Cookie utilizzati:</strong> {category.examples}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="p-4 flex flex-col sm:flex-row gap-3" style={{ background: '#F9FAFB', borderTop: '1px solid #E5E7EB' }}>
            <button
              onClick={() => setShowCustomize(!showCustomize)}
              className="flex-1 py-3 px-4 rounded-xl font-medium text-sm flex items-center justify-center gap-2 transition-colors"
              style={{ background: 'white', border: '1px solid #E5E7EB', color: '#2A9FCB' }}
            >
              <Settings className="w-4 h-4" />
              {showCustomize ? 'Nascondi Opzioni' : 'Personalizza'}
            </button>
            
            {showCustomize ? (
              <button
                onClick={saveCustomPreferences}
                className="flex-1 py-3 px-4 rounded-xl font-medium text-sm text-white flex items-center justify-center gap-2 transition-colors"
                style={{ background: '#2A9FCB' }}
              >
                <Check className="w-4 h-4" />
                Salva Preferenze
              </button>
            ) : (
              <>
                <button
                  onClick={rejectAll}
                  className="flex-1 py-3 px-4 rounded-xl font-medium text-sm transition-colors"
                  style={{ background: 'white', border: '1px solid #E5E7EB', color: '#6B7280' }}
                >
                  Rifiuta Tutti
                </button>
                <button
                  onClick={acceptAll}
                  className="flex-1 py-3 px-4 rounded-xl font-medium text-sm text-white transition-colors"
                  style={{ background: 'linear-gradient(135deg, #2A9FCB 0%, #2A9FCB 100%)' }}
                >
                  Accetta Tutti
                </button>
              </>
            )}
          </div>

          {/* Legal Notice */}
          <div className="px-4 pb-4 pt-0" style={{ background: '#F9FAFB' }}>
            <p className="text-[10px] text-[#2A9FCB]/60 text-center">
              Ai sensi dell'art. 122 D.Lgs. 196/2003, delle Linee Guida del Garante Privacy del 10/06/2021 
              e del Reg. UE 2016/679 (GDPR). Il tuo consenso è valido per 12 mesi.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default CookieBanner;
